/**
 * Shipping-protection A/B in the cart totals (drawer + /cart) — ab.hc.tools `custom` experiment, Oct 2026.
 *
 * 1. Arm: reads the visitor's assignment (window.__HCAB, synchronous in <head>) and sets
 *    <html data-afl-protect="control|basic">; afroyla-cart-protection.css shows that arm's card.
 *    QA: ?afl_protect=basic|control forces an arm for this tab (sessionStorage; exposure is NOT counted),
 *    ?afl_protect=off clears it.
 * 2. Exposure: counted for EVERY arm (custom counts nothing otherwise) the first time the visitor sees the cart
 *    totals WITH the protection blocks rendered (they render for every arm, hidden for control) — the open cart
 *    drawer or /cart. An empty cart or a missing fee product renders no block, so it counts in no arm.
 * 3. Checkout: "Protected checkout" swaps any protection line for the fee variant Liquid rendered, then goes to
 *    /checkout; "Continue without protection" removes any protection line, then goes to /checkout. The same
 *    call writes the A/B attribution attributes (the page leaves before ab.js / afroyla-hcab-attribution.js can).
 *    A failed or slow (> 4 s) cart call never blocks the purchase — the shopper still lands on /checkout.
 * 4. Stale fee: a fee line only has to live between that click and /checkout. Whenever a theme page loads (or is
 *    restored from the back-forward cache) with a fee line still in the cart, it is removed in EVERY arm, so a
 *    shopper back from checkout is never charged a fee the cart doesn't show. Keep this file live after the
 *    test ends for the same reason (carts live ~14 days).
 * The fee line carries `_is_protection` (hidden at checkout and skipped by every cart template's line list/count).
 */
(function () {
  if (window.__aflProtect) return;
  window.__aflProtect = true;

  var EXP = '909d19aa-ec84-4728-916f-b44285f957f4';
  var ARMS = {
    '4ebeba70-633b-4710-b222-691203a22fba': 'control',
    '6ffbfe9e-405d-4914-8e30-c9bf82c3331f': 'basic',
  };
  var QA_KEY = 'afl_protect_qa';
  var TIMEOUT_MS = 4000;
  var root = document.documentElement;
  var arm = 'control';
  var qa = null;

  function readQa() {
    try {
      var q = new URLSearchParams(window.location.search).get('afl_protect');
      if (q === 'off') window.sessionStorage.removeItem(QA_KEY);
      else if (q === 'basic' || q === 'control') window.sessionStorage.setItem(QA_KEY, q);
      return window.sessionStorage.getItem(QA_KEY);
    } catch (e) {
      return null;
    }
  }

  function assignment() {
    var hc = window.__HCAB;
    return hc && hc.assignments ? hc.assignments[EXP] : null;
  }

  function resolveArm() {
    qa = readQa();
    arm = qa || ARMS[assignment()] || 'control';
    root.setAttribute('data-afl-protect', arm);
  }

  /* ---- exposure ---------------------------------------------------------------------------------------- */
  var exposed = false;
  function expose() {
    if (exposed || qa) return;
    var hc = window.__HCAB;
    if (!assignment() || !hc || typeof hc.exposure !== 'function') return;
    exposed = true;
    try { hc.exposure(EXP); } catch (e) { /* never break the cart over analytics */ }
  }
  function cartDrawerOpen() {
    var d = document.querySelector('cart-drawer-component');
    d = d && d.closest('dialog');
    return !!(d && d.hasAttribute('open') && d.querySelector('.afl-protect'));
  }
  function onCartPage() {
    return /^\/cart\/?$/.test(window.location.pathname) && !!document.querySelector('.afl-protect');
  }
  function maybeExpose() {
    if (onCartPage() || cartDrawerOpen()) expose();
  }

  resolveArm();
  /* preview links resolve the assignment after load */
  document.addEventListener('hcab:ready', function () { resolveArm(); maybeExpose(); });
  maybeExpose();
  /* the drawer's <dialog> gaining [open], or its totals being re-rendered while open (cart was empty before) */
  var watcher = new MutationObserver(function () {
    maybeExpose();
    if (exposed) watcher.disconnect();
  });
  if (!exposed) watcher.observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['open'] });

  /* ---- cart calls ---------------------------------------------------------------------------------------- */
  function request(url, body, signal) {
    return fetch(url, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
      signal: signal,
    }).then(function (r) {
      if (!r.ok) throw new Error(url + ' ' + r.status);
      return r.json();
    });
  }

  /* A/B attribution on the cart (same shape as afroyla-seen-buy.js abAttributes()). */
  function abAttributes() {
    try {
      var all = typeof window.aflHcabAssignments === 'function' ? window.aflHcabAssignments() : (window.__HCAB && window.__HCAB.assignments) || {};
      var ids = Object.keys(all || {});
      var m = document.cookie.match(/(?:^|;\s*)_hcab_vid=([^;]+)/);
      if (!ids.length || !m) return null;
      return { _ab_visitor: decodeURIComponent(m[1]), _ab_assignments: ids.map(function (id) { return id + ':' + all[id]; }).join(',') };
    } catch (e) {
      return null;
    }
  }

  /* updates{} that zero every protection line in `cart` (null when there is none) */
  function protectionZeroes(cart) {
    var updates = {};
    var n = 0;
    (cart.items || []).forEach(function (item) {
      if (item.properties && item.properties._is_protection) { updates[item.key] = 0; n++; }
    });
    return n ? updates : null;
  }

  /* ---- stale fee cleanup (every arm, every theme page) ----------------------------------------------------- */
  /* Order-level marker CS reads in the admin order page ("Additional details") — replaces a Shopify Flow tag:
     set on every arm-button click, cleared together with a stale fee line. */
  var ORDER_ATTR = 'Shipping protection';
  function orderAttr(feeVariantId) {
    if (!feeVariantId) return 'Declined';
    return 'Protection';
  }

  var cleaning = false;
  var busy = false;
  function removeStaleFee() {
    if (cleaning || busy) return;
    cleaning = true;
    request('/cart.js')
      .then(function (cart) {
        var updates = protectionZeroes(cart);
        if (!updates) return false;
        var cleared = {};
        cleared[ORDER_ATTR] = '';
        return request('/cart/update.js', { updates: updates, attributes: cleared }).then(function () { return true; });
      })
      .then(function (changed) {
        /* re-render the drawer/cart so totals, counts and the card reflect the cart without the fee */
        if (changed) document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
      })
      .catch(function () {})
      .then(function () { cleaning = false; });
  }
  if (document.querySelector('[data-afl-protect-stale]')) removeStaleFee();

  /* ---- checkout ------------------------------------------------------------------------------------------- */
  function buttons() {
    return document.querySelectorAll('[data-afl-protect-go], [data-afl-protect-skip]');
  }
  function setBusy(on) {
    busy = on;
    buttons().forEach(function (b) { b.disabled = on; });
  }

  function goToCheckout(feeVariantId) {
    if (busy) return;
    setBusy(true);
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = setTimeout(function () { if (ctrl) ctrl.abort(); }, TIMEOUT_MS);
    var signal = ctrl ? ctrl.signal : undefined;
    request('/cart.js', null, signal)
      .then(function (cart) {
        var body = {};
        var updates = protectionZeroes(cart);
        var attrs = abAttributes() || {};
        attrs[ORDER_ATTR] = orderAttr(feeVariantId);
        if (updates) body.updates = updates;
        body.attributes = attrs;
        return request('/cart/update.js', body, signal);
      })
      .then(function () {
        if (!feeVariantId) return null;
        return request('/cart/add.js', {
          items: [{ id: Number(feeVariantId), quantity: 1, properties: { _is_protection: 'true', _protection_arm: arm } }],
        }, signal);
      })
      .catch(function (e) { if (window.console) console.warn('[afl-protect]', e); })
      .then(function () {
        clearTimeout(timer);
        window.location.href = '/checkout';
      });
  }

  /* the fee variant of the arm the visitor sees, in the block closest to `el` (drawer or /cart page) */
  function visibleGo(scope) {
    var block = (scope || document).querySelector('.afl-protect[data-afl-protect-arm="' + arm + '"] [data-afl-protect-go]');
    return block ? block.getAttribute('data-afl-protect-go') : null;
  }

  document.addEventListener('click', function (e) {
    var go = e.target.closest('[data-afl-protect-go]');
    if (go) { e.preventDefault(); goToCheckout(go.getAttribute('data-afl-protect-go')); return; }
    var skip = e.target.closest('[data-afl-protect-skip]');
    if (skip) { e.preventDefault(); goToCheckout(null); }
  });

  /* Enter in a quantity field submits #cart-form through the (hidden) #checkout button: in a protected arm,
     route it through the pre-selected protected checkout instead of skipping the choice. */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.id !== 'cart-form' || arm === 'control') return;
    var ready = document.querySelector('.afl-cart-checkout--protect-ready');
    if (!ready) return;
    var fee = visibleGo(form.closest('.afl-cart-drawer, .afl-cart') || document);
    if (!fee) return;
    e.preventDefault();
    goToCheckout(fee);
  }, true);

  /* Back from checkout restores this page from the bfcache: un-stick the buttons and drop the fee line. */
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    setBusy(false);
    removeStaleFee();
  });
})();
